/**
 * Cookie Consent Banner Management
 *
 * Handles user cookie preferences and banner display logic
 * Stores preferences in localStorage for persistence across sessions
 */

const CONSENT_KEY = "cookieConsent";
const CONSENT_TIMESTAMP_KEY = "cookieConsentTimestamp";
const CONSENT_EXPIRY_DAYS = 365; // Cookie consent lasts for 1 year

export default function cookieConsent() {
  return {
    showBanner: false,

    init() {
      // Check if user has already made a choice
      const consent = this.getConsentStatus();

      if (consent === null) {
        // No consent recorded, show banner after a short delay for better UX
        setTimeout(() => {
          this.showBanner = true;
        }, 1000);
      } else {
        // User has already made a choice
        this.applyConsentPreferences(consent);
      }

      // Check if consent has expired
      this.checkConsentExpiry();
    },

    /**
     * Get the current consent status from localStorage
     * @returns {boolean|null} true if accepted, false if rejected, null if not set
     */
    getConsentStatus() {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent === null) return null;
      return consent === "accepted";
    },

    /**
     * Check if the consent has expired and reset if needed
     */
    checkConsentExpiry() {
      const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);
      if (!timestamp) return;

      const consentDate = new Date(parseInt(timestamp));
      const expiryDate = new Date(consentDate);
      expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);

      if (new Date() > expiryDate) {
        // Consent has expired, reset
        localStorage.removeItem(CONSENT_KEY);
        localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
        this.showBanner = true;
      }
    },

    /**
     * User accepts all cookies
     */
    acceptCookies() {
      this.setConsent("accepted");
      this.showBanner = false;
      this.applyConsentPreferences(true);
      this.triggerConsentEvent("accepted");
    },

    /**
     * User rejects non-essential cookies
     */
    rejectCookies() {
      this.setConsent("rejected");
      this.showBanner = false;
      this.applyConsentPreferences(false);
      this.triggerConsentEvent("rejected");
    },

    /**
     * Store the consent choice
     * @param {string} status - 'accepted' or 'rejected'
     */
    setConsent(status) {
      localStorage.setItem(CONSENT_KEY, status);
      localStorage.setItem(CONSENT_TIMESTAMP_KEY, Date.now().toString());

      // Also set a cookie for server-side access if needed
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + CONSENT_EXPIRY_DAYS);
      document.cookie = `${CONSENT_KEY}=${status}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    },

    /**
     * Apply user's consent preferences to third-party services
     * @param {boolean} accepted - Whether cookies were accepted
     */
    applyConsentPreferences(accepted) {
      if (accepted) {
        // Enable analytics and marketing cookies
        this.enableAnalytics();
      } else {
        // Disable or remove non-essential cookies
        this.disableAnalytics();
      }
    },

    /**
     * Enable analytics tracking
     */
    enableAnalytics() {
      // Add your analytics initialization here
      // Example: Google Analytics, Mixpanel, etc.
      console.log("Analytics enabled");

      // If you have Google Analytics, you can enable it here:
      // if (window.gtag) {
      //   gtag('consent', 'update', {
      //     'analytics_storage': 'granted'
      //   });
      // }
    },

    /**
     * Disable analytics tracking
     */
    disableAnalytics() {
      console.log("Analytics disabled");

      // If you have Google Analytics, you can disable it here:
      // if (window.gtag) {
      //   gtag('consent', 'update', {
      //     'analytics_storage': 'denied'
      //   });
      // }
    },

    /**
     * Trigger a custom event for other parts of the app to listen to
     * @param {string} status - 'accepted' or 'rejected'
     */
    triggerConsentEvent(status) {
      const event = new CustomEvent("cookieConsentUpdated", {
        detail: { status },
      });
      window.dispatchEvent(event);
    },
  };
}

// Export the consent status checker for use in other modules
export function hasConsent() {
  const consent = localStorage.getItem(CONSENT_KEY);
  return consent === "accepted";
}

// Export function to check if user has made a choice
export function hasConsentChoice() {
  return localStorage.getItem(CONSENT_KEY) !== null;
}
